import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import { auth, db } from "../lib/firebase";
import { useStore } from "../store";
import { AlertCircle } from "lucide-react";
import { doc, getDoc } from 'firebase/firestore';
import { useRipple } from '../lib/useRipple';
import { useTranslation } from '../i18n/useTranslation';

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const setUser = useStore((state) => state.setUser);
  const { createRipple } = useRipple();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // 1. Authenticate user
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      // 2. Check user profile
      const profileRef = doc(db, 'profiles', user.uid);
      const profileSnap = await getDoc(profileRef);

      if (!profileSnap.exists()) {
        // No profile exists - redirect to registration
        return navigate('/register');
      }

      // 3. Set user and navigate to dashboard
      setUser(user);
      const from = location.state?.from?.pathname || '/dashboard';
      return navigate(from);
    } catch (error) {
      console.error('Login error:', error);
      setError(
        error instanceof Error ? error.message : t('auth.invalidEmailPassword')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError(t('auth.enterEmailAddress'));
      return;
    }

    setResetLoading(true);
    setError('');

    try {
      await sendPasswordResetEmail(auth, email);
      setResetEmailSent(true);
    } catch {
      setError(
        t('auth.passwordResetFailed')
      );
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div
      className='min-h-screen pt-16 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8'
      style={{
        backgroundImage: `url(/GOALR-Login-background.png)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className='glass-card max-w-md w-full p-8 animate-float'>
        <Link
          to='/'
          className='block text-center mb-8 transition-colors hover:scale-105 transform duration-200'
        >
          <img
            src='https://cdn.shopify.com/s/files/1/0609/6397/9463/files/Logo_Goalr.png?v=1740666570'
            alt='GoalR Logo'
            className='h-14 w-auto mx-auto'
          />
        </Link>
        <h2 className='text-3xl font-bold text-center mb-8 text-primary-600 italic'>
          {t('auth.signIn')}
        </h2>
        {error && (
          <div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-start'>
            <AlertCircle className='h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0' />
            <span>{error}</span>
          </div>
        )}
        {resetEmailSent && (
          <div className='bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4'>
            {t('auth.resetPasswordSent')}
          </div>
        )}
        <form onSubmit={handleSubmit} className='space-y-6'>
          <div>
            <label
              htmlFor='email'
              className='block text-sm font-medium text-heading mb-2'
            >
              {t('auth.email')}
            </label>
            <input
              type='email'
              id='email'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className='w-full px-4 py-3 rounded-lg border border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600 focus:ring-2 transition-all duration-200'
              required
              disabled={loading}
            />
          </div>
          <div>
            <label
              htmlFor='password'
              className='block text-sm font-medium text-heading mb-2'
            >
              {t('auth.password')}
            </label>
            <input
              type='password'
              id='password'
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className='w-full px-4 py-3 rounded-lg border border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600 focus:ring-2 transition-all duration-200'
              required
              disabled={loading}
            />
          </div>
          <div className='flex justify-end'>
            <button
              type='button'
              onClick={(e) => {
                createRipple(e);
                handleForgotPassword();
              }}
              className='text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200'
              disabled={resetLoading}
            >
              {resetLoading ? t('auth.sending') : t('auth.forgotPassword')}
            </button>
          </div>
          <button
            type='submit'
            onClick={createRipple}
            className='btn-mui-primary btn-ripple w-full'
            disabled={loading}
          >
            {loading ? t('auth.signingIn') : t('auth.signIn')}
          </button>
        </form>
        <p className='mt-6 text-center text-muted'>
          {t('auth.dontHaveAccount')}{' '}
          <Link
            to='/register'
            className='text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200'
          >
            {t('auth.signUp')}
          </Link>
        </p>
      </div>
    </div>
  );
}